/**
 * The Dutch energy label classes for homes (NTA 8800, labels registered from
 * 1 January 2021), from most to least efficient. `max` is the upper bound of
 * the home's primary fossil energy use in kWh per m² a year; G is open ended.
 */
export const ENERGY_LABEL_CLASSES = [
  { label: "A++++", max: 0, color: "#167a45", ink: "#ffffff" },
  { label: "A+++", max: 50, color: "#1f8a4c", ink: "#ffffff" },
  { label: "A++", max: 75, color: "#2c9a51", ink: "#ffffff" },
  { label: "A+", max: 105, color: "#43a957", ink: "#ffffff" },
  { label: "A", max: 160, color: "#5fb85c", ink: "#ffffff" },
  { label: "B", max: 190, color: "#a3cc55", ink: "#1e1e1e" },
  { label: "C", max: 250, color: "#d7d648", ink: "#1e1e1e" },
  { label: "D", max: 290, color: "#f4c542", ink: "#1e1e1e" },
  { label: "E", max: 335, color: "#f1a03b", ink: "#1e1e1e" },
  { label: "F", max: 380, color: "#e8753a", ink: "#1e1e1e" },
  { label: "G", max: null, color: "#d6453a", ink: "#ffffff" },
] as const;

export type EnergyLabelClass = (typeof ENERGY_LABEL_CLASSES)[number];

/** Index of a registered label ("A+++", "a ++", "B") on the scale, or -1. */
export const getEnergyLabelIndex = (label: string | null | undefined) => {
  if (!label) return -1;
  const normalized = label.replace(/\s/g, "").toUpperCase();
  return ENERGY_LABEL_CLASSES.findIndex((item) => item.label === normalized);
};

/** Colour pair for a label tile; unknown labels fall back to neutral ink. */
export const getEnergyLabelColors = (label: string | null | undefined) => {
  const item = ENERGY_LABEL_CLASSES[getEnergyLabelIndex(label)];
  return item
    ? { background: item.color, color: item.ink }
    : { background: "#f4f4f5", color: "#1e1e1e" };
};

/** "≤ 160" for a class, "> 380" for G. */
export const formatEnergyLabelBound = (index: number, locale: string) => {
  const item = ENERGY_LABEL_CLASSES[index];
  const number = new Intl.NumberFormat(locale);
  if (item.max !== null) return `≤ ${number.format(item.max)}`;
  const previous = ENERGY_LABEL_CLASSES[index - 1];
  return `> ${number.format(previous.max ?? 0)}`;
};
